/**
 * servicoApiProcessos
 *
 * RESPONSABILIDADE:
 * - Encapsular chamadas HTTP para os endpoints de persistência de processos
 *   (POST /api/processos, GET /api/processos, GET /api/processos/{id}).
 *
 * CONTEXTO DE NEGÓCIO:
 * O frontend precisa criar, listar e recuperar o estado de "processos de
 * análise" para permitir re-hidratação de sessão (continuar onde parou),
 * exibir dashboard de processos e associar uploads/artefatos ao processo.
 *
 * NOTA:
 * - Este serviço é deliberadamente simples e tolerante: caso a API não esteja
 *   disponível (ex: ambiente de desenvolvimento sem backend), as chamadas
 *   irão lançar erro e o código chamador deve prover fallback (ex: localStorage).
 */

import type { AxiosResponse } from 'axios';
import { clienteApi } from '../../../core/servicos/clienteApi';

// Base específica para os endpoints de processos (clienteApi já define baseURL)
export const PROCESSOS_API_BASE = `/api/juridico/processos/`;

/**
 * Tipo representando o payload retornado pela API de processos.
 * Campos opcionais são permissivos pois ambientes de teste podem variar.
 */
export interface ProcessoApi {
  processo_id: string;
  usuario_id?: string;
  nome?: string;
  descricao?: string;
  estado_wizard?: string; // ex: 'upload', 'documentos_complementares', 'selecionar_agentes', 'processing', 'done'
  status_processamento?: string; // ex: 'pending' | 'processing' | 'failed' | 'done'
  metadados?: Record<string, unknown>; // Estrutura livre: lista de docs, peticao_id, agentes selecionados etc.
  criado_em?: string;
  tipo_vertical?: string; // TAREFA-224: Tipo de vertical/fluxo do processo
  progresso?: number; // TAREFA-224: Progresso percentual (0-100)
  etapa_atual?: number; // TAREFA-224: Índice da etapa atual (0-5)
  // MULTI-TENANT: Campos de organização
  organizacao_id?: string; // ID da organização (se compartilhado)
  compartilhado_com_org?: boolean; // Se está compartilhado com a org
  nome_usuario?: string; // Nome do usuário que criou o processo
  eh_meu?: boolean; // Se o processo é do próprio usuário autenticado
}

// ===== Tipos: Edição de Minuta (alinhado ao backend/src/api/modelos_processos.py) =====

export type TipoOperacaoEdicao = 'replace' | 'insert' | 'delete';

export interface OperacaoEdicaoMinuta {
  tipo: TipoOperacaoEdicao;
  offset_inicio: number; // 0-based
  offset_fim?: number; // necessário para replace/delete
  novo_conteudo?: string; // necessário para insert/replace
}

/**
 * Cria/inicia um novo processo no backend.
 * Retorna o objeto criado (incluindo `processo_id`).
 * 
 * TAREFA-224: Agora aceita `tipo_vertical` para diferenciar fluxos
 */
export async function criarProcesso(
  dados: { nome?: string; descricao?: string; usuario_id?: string; tipo_vertical?: string }
): Promise<AxiosResponse<ProcessoApi>> {
  return clienteApi.post<ProcessoApi>(
    `${PROCESSOS_API_BASE}`,
    dados
  );
}

/**
 * Lista processos do usuário (ou todos, se sem filtro).
 * A API pode retornar { processos: [...] } ou um array direto; a função
 * chamadora deve tratar formatos diferentes.
 */
export interface ProcessoListItem {
  processo_id: string;
  nome?: string;
  usuario_id?: string;
  estado_wizard?: string;
  status_processamento?: string;
  criado_em?: string;
  peticoes?: string[];
  metadados?: Record<string, unknown>;
  tipo_vertical?: string; // TAREFA-224: Tipo de vertical/fluxo do processo
}

export interface ProcessoListResponsePayload {
  sucesso: boolean;
  total: number;
  processos: ProcessoListItem[];
}

export async function listarProcessos(): Promise<AxiosResponse<ProcessoListResponsePayload>> {
  return clienteApi.get(`${PROCESSOS_API_BASE}`);
}

/**
 * Obtém um processo específico pelo seu id (processo_id).
 * Retorna o objeto completo com metadados e estado do wizard.
 */
export async function obterProcesso(processoId: string): Promise<AxiosResponse<ProcessoApi>> {
  const resp = await clienteApi.get(`${PROCESSOS_API_BASE}${encodeURIComponent(processoId)}`);
  // Normalizar formato: alguns backends retornam { sucesso, processo: {...} }
  // enquanto em modos fallback pode retornar diretamente o objeto do processo.
  const payload = resp.data && resp.data.processo ? resp.data.processo : resp.data;
  // Retornar um objeto similar ao AxiosResponse, mas com `data` normalizado para o processo
  return { ...resp, data: payload } as AxiosResponse<ProcessoApi>;
}

/**
 * Atualiza metadados ou estado do processo no backend.
 */
export async function atualizarProcesso(
  processoId: string,
  dados: { metadados?: Record<string, unknown>; estado_wizard?: string; status_processamento?: string; nome?: string; descricao?: string }
): Promise<AxiosResponse<ProcessoApi>> {
  return clienteApi.patch<ProcessoApi>(`${PROCESSOS_API_BASE}${encodeURIComponent(processoId)}`, dados);
}

/**
 * Marca um processo como arquivado (soft-delete).
 */
export async function arquivarProcesso(processoId: string): Promise<AxiosResponse<ProcessoApi>> {
  return clienteApi.patch<ProcessoApi>(`${PROCESSOS_API_BASE}${encodeURIComponent(processoId)}/arquivar`);
}

/**
 * Exclui permanentemente um processo.
 */
export async function excluirProcesso(processoId: string): Promise<AxiosResponse<void>> {
  return clienteApi.delete<void>(`${PROCESSOS_API_BASE}${encodeURIComponent(processoId)}`);
}

// Exportar o base url para eventuais logs/diagnósticos
// OBS: `PROCESSOS_API_BASE` exportado no topo do arquivo para uso em logs/diagnósticos

/**
 * Aplica operações de edição sobre a minuta de um processo (PATCH /api/processos/{id}/minuta).
 * Quando `id_minuta` for omitido, o backend aplicará na `minuta_recomendada_id` do processo.
 */
export async function editarMinuta(
  processoId: string,
  operacoes: OperacaoEdicaoMinuta[],
  idMinuta?: string
): Promise<AxiosResponse<{ sucesso: boolean; id_minuta: string; conteudo_markdown: string }>> {
  return clienteApi.patch(`${PROCESSOS_API_BASE}${encodeURIComponent(processoId)}/minuta`, {
    id_minuta: idMinuta,
    operacoes,
  });
}
